import {HttpsCallable} from '@firebase/functions-types';
import {getFunctions, httpsCallable} from 'firebase/functions';
import {AttributionContext, AttributionData, determineAttribution} from '@castiron/utils';
import FirebaseApp from '../firebase/initFirebase';
import {Attribution} from '@castiron/domain';
import {useContext} from 'react';
import {getAuth} from "@firebase/auth";
import axios from "axios";

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const MARKETPLACE_ID = process.env.REACT_APP_MARKETPLACE_ID;

type BoundedContextName =
  | 'accounts'
  | 'asana'
  | 'auth'
  | 'content'
  | 'customers'
  | 'firestore'
  | 'images'
  | 'locations'
  | 'marketing'
  | 'messaging'
  | 'orders'
  | 'packages'
  | 'presales'
  | 'products'
  | 'shops'
  | 'stripe'
  | 'subscriptions'
  | 'test'
  | 'transactions'
  | 'utils';

interface ServiceConfig {
  type?: 'call' | 'request';
  version?: number;
  attribution?: Attribution;
}

const DEFAULT_CONFIG: ServiceConfig = {
  type: 'call',
  version: 2,
};

const FUNCTIONS_STUB = {
  httpsCallable: (message: any): HttpsCallable => {
    console.warn('Using stub httpsCallable implementation');
    return data => Promise.resolve({ data: {} });
  },
};

const useStub = FirebaseApp?.firebase?.apps.length === 0;
const functions = useStub ? undefined : getFunctions(FirebaseApp);

export const getServiceUrl = (boundedContext: BoundedContextName, serviceName: string) => {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID;
  const isLocal = process.env.REACT_APP_IS_LOCAL_FIREBASE || process.env.NEXT_PUBLIC_IS_LOCAL_FIREBASE;
  return isLocal === 'true'
    ? `http://localhost:5001/castiron-localdev/us-central1/${boundedContext}-${serviceName}`
    : `https://us-central1-${projectId}.cloudfunctions.net/${boundedContext}-${serviceName}`;
};

export const getRequestService = (boundedContext: BoundedContextName, serviceName: string,
                                  config: ServiceConfig = DEFAULT_CONFIG,) => {
  const serviceUrl = getServiceUrl(boundedContext, serviceName);

  return async (request: Record<string, any>): Promise<any> => {
    const attribution = config.attribution || determineAttribution({ gaStreamId: GA_MEASUREMENT_ID });

    const token = await getAuth().currentUser?.getIdToken();

    const name = serviceName.toLowerCase();
    console.debug(`Requesting Service [${name}]`, request);
    const resp = await axios.post(
      serviceUrl,
      {
        ...request,
        attribution,
        experiments: {},
        marketplace: MARKETPLACE_ID,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      },
    );
    console.debug(`Response from request to Service [${name}]`, resp.data);
    return resp.data;
  };
};

export const getCallService = (
  boundedContext: BoundedContextName,
  serviceName: string,
  config: ServiceConfig = DEFAULT_CONFIG,
) => {
  return async (request: Record<string, any>) => {
    const name = serviceName.toLowerCase();
    const att = config?.attribution || determineAttribution({ gaStreamId: GA_MEASUREMENT_ID });
    const call = useStub
      ? FUNCTIONS_STUB.httpsCallable(`${boundedContext}-${name}`)
      : httpsCallable(functions, `${boundedContext}-${name}`);
    console.debug(`Calling Service [${name}]`, request);
    const result = await call({
      ...request,
      attribution: att,
      marketplace: MARKETPLACE_ID,
    });
    console.debug(`Response from call to Service [${name}]`, result);
    return result.data;
  };
};

export const getService = (
  boundedContext: BoundedContextName,
  serviceName: string,
  config: ServiceConfig = DEFAULT_CONFIG,
) => config.type === 'request'
  ? getRequestService(boundedContext, serviceName, config)
  : getCallService(boundedContext, serviceName, config);

export const useService = (
  boundedContext: BoundedContextName,
  serviceName: string,
  config: ServiceConfig = DEFAULT_CONFIG,
) => {
  const { attribution } = useContext<AttributionData>(AttributionContext);
  return config.type === 'request'
    ? getRequestService(boundedContext, serviceName, {...config, attribution})
    : getCallService(boundedContext, serviceName, {...config, attribution});
};
